import { useState } from "react";
import { collection, getDocs, getDoc, doc, query, where } from "firebase/firestore";
import getFirebase from "../../utilities/firebase";
import { Timestamp } from "firebase/firestore";
import { createLogger } from "../../utilities/logger";

const log = createLogger("ExportProductionUsersToDemoTool");

const COMPONENT_CLASS = "c-export-production-users-to-demo-tool";

const DEMO_ORGANIZATION_ID = "4PFeK2nilNGSiwXejB2o";
const DEMO_GROUP_ID = "RG4FlsM0b5AqbUgpmH5Z";
const DEMO_GROUP_NAME = "Demo Group 1";

interface UserWpPair {
    userId: string;
    wellnessProgramId: string;
    index: number;
}

const anonymizeName = (): { firstName: string; lastName: string } => {
    const names = [
        { first: "Michael", last: "Johnson" }, { first: "Michael", last: "Williams" }, { first: "Michael", last: "Brown" }, { first: "Michael", last: "Jones" }, { first: "Michael", last: "Garcia" },
        { first: "Sarah", last: "Johnson" }, { first: "Sarah", last: "Williams" }, { first: "Sarah", last: "Brown" }, { first: "Sarah", last: "Jones" }, { first: "Sarah", last: "Garcia" },
        { first: "James", last: "Johnson" }, { first: "James", last: "Williams" }, { first: "James", last: "Brown" }, { first: "James", last: "Jones" }, { first: "James", last: "Garcia" },
        { first: "Emma", last: "Johnson" }, { first: "Emma", last: "Williams" }, { first: "Emma", last: "Brown" }, { first: "Emma", last: "Jones" }, { first: "Emma", last: "Garcia" },
        { first: "David", last: "Miller" }, { first: "David", last: "Davis" }, { first: "David", last: "Rodriguez" }, { first: "David", last: "Martinez" }, { first: "David", last: "Wilson" },
        { first: "Olivia", last: "Miller" }, { first: "Olivia", last: "Davis" }, { first: "Olivia", last: "Rodriguez" }, { first: "Olivia", last: "Martinez" }, { first: "Olivia", last: "Wilson" },
        { first: "Robert", last: "Anderson" }, { first: "Robert", last: "Taylor" }, { first: "Robert", last: "Thomas" }, { first: "Robert", last: "Moore" }, { first: "Robert", last: "Jackson" },
        { first: "Sophia", last: "Anderson" }, { first: "Sophia", last: "Taylor" }, { first: "Sophia", last: "Thomas" }, { first: "Sophia", last: "Moore" }, { first: "Sophia", last: "Jackson" },
        { first: "William", last: "Martin" }, { first: "William", last: "Lee" }, { first: "William", last: "Thompson" }, { first: "William", last: "White" }, { first: "William", last: "Harris" },
        { first: "Isabella", last: "Martin" }, { first: "Isabella", last: "Lee" }, { first: "Isabella", last: "Thompson" }, { first: "Isabella", last: "White" }, { first: "Isabella", last: "Harris" },
        { first: "Alexander", last: "Clark" }, { first: "Alexander", last: "Lewis" }, { first: "Alexander", last: "Robinson" }, { first: "Alexander", last: "Walker" }, { first: "Alexander", last: "Young" },
        { first: "Ava", last: "Clark" }, { first: "Ava", last: "Lewis" }, { first: "Ava", last: "Robinson" }, { first: "Ava", last: "Walker" }, { first: "Ava", last: "Young" },
        { first: "Daniel", last: "Allen" }, { first: "Daniel", last: "King" }, { first: "Daniel", last: "Wright" }, { first: "Daniel", last: "Scott" }, { first: "Daniel", last: "Green" },
        { first: "Mia", last: "Allen" }, { first: "Mia", last: "King" }, { first: "Mia", last: "Wright" }, { first: "Mia", last: "Scott" }, { first: "Mia", last: "Green" },
        { first: "Matthew", last: "Baker" }, { first: "Matthew", last: "Adams" }, { first: "Matthew", last: "Nelson" }, { first: "Matthew", last: "Hill" }, { first: "Matthew", last: "Ramirez" },
        { first: "Charlotte", last: "Baker" }, { first: "Charlotte", last: "Adams" }, { first: "Charlotte", last: "Nelson" }, { first: "Charlotte", last: "Hill" }, { first: "Charlotte", last: "Ramirez" },
        { first: "Ethan", last: "Campbell" }, { first: "Ethan", last: "Mitchell" }, { first: "Ethan", last: "Roberts" }, { first: "Ethan", last: "Carter" }, { first: "Ethan", last: "Phillips" },
        { first: "Amelia", last: "Campbell" }, { first: "Amelia", last: "Mitchell" }, { first: "Amelia", last: "Roberts" }, { first: "Amelia", last: "Carter" }, { first: "Amelia", last: "Phillips" },
        { first: "Benjamin", last: "Evans" }, { first: "Benjamin", last: "Turner" }, { first: "Benjamin", last: "Torres" }, { first: "Benjamin", last: "Parker" }, { first: "Benjamin", last: "Collins" },
        { first: "Harper", last: "Evans" }, { first: "Harper", last: "Turner" }, { first: "Harper", last: "Torres" }, { first: "Harper", last: "Parker" }, { first: "Harper", last: "Collins" },
        { first: "Sebastian", last: "Edwards" }, { first: "Sebastian", last: "Stewart" }, { first: "Sebastian", last: "Flores" }, { first: "Sebastian", last: "Morris" }, { first: "Sebastian", last: "Nguyen" }
    ];
    const randomIndex = Math.floor(Math.random() * names.length);
    return {
        firstName: names[randomIndex].first,
        lastName: names[randomIndex].last
    };
};

const serializeValue = (value: any): any => {
    if (value instanceof Timestamp) {
        return { seconds: value.seconds, nanoseconds: value.nanoseconds };
    }
    return value;
};

const ExportProductionUsersToDemoTool: React.FC = () => {
    const [input, setInput] = useState("");
    const [duplicates, setDuplicates] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [results, setResults] = useState<string[]>([]);
    const [exportedData, setExportedData] = useState<any>(null);

    const parseInput = (): UserWpPair[] => {
        const lines = input.trim().split("\n");
        const pairs: UserWpPair[] = [];

        lines.forEach((line, index) => {
            const parts = line.trim().split(/[,\t\s]+/);
            if (parts.length >= 2) {
                pairs.push({
                    userId: parts[0].trim(),
                    wellnessProgramId: parts[1].trim(),
                    index
                });
            }
        });

        return pairs;
    };

    const exportUsers = async () => {
        setIsExporting(true);
        setResults([]);

        const pairs = parseInput();
        if (pairs.length === 0) {
            setResults(["No valid user/wellnessProgramId pairs found. Format: userId, wellnessProgramId (one per line)"]);
            setIsExporting(false);
            return;
        }

        const dupCount = parseInt(duplicates.toString(), 10) || 0;
        const totalCount = pairs.length + dupCount;

        const { db } = getFirebase();

        const users: any[] = [];
        const wellnessPrograms: any[] = [];
        const mskScores: any[] = [];
        const correctivePrograms: any[] = [];
        const focusArticles: any[] = [];

        try {
            // First, process all original pairs and store base data
            const baseUsers: any[] = [];
            const baseWPs: any[] = [];
            const baseMsks: any[] = [];
            const baseCPs: any[] = [];
            const baseFAs: any[] = [];

            for (let origIdx = 0; origIdx < pairs.length; origIdx++) {
                const pair = pairs[origIdx];
                log.debug(`[Export] Base ${origIdx + 1}: input userId=${pair.userId}, wellnessProgramId=${pair.wellnessProgramId}`);
                setResults(prev => [...prev, `Processing base ${origIdx + 1}: ${pair.userId}`]);

                const userDoc = await getDoc(doc(db, "users", pair.userId));
                if (!userDoc.exists()) {
                    log.debug(`[Export] User NOT FOUND: ${pair.userId}`);
                    setResults(prev => [...prev, `  User not found: ${pair.userId}`]);
                    continue;
                }
                log.debug(`[Export] Fetched user: ${userDoc.id}`);
                const userData = userDoc.data();

                const wpDoc = await getDoc(doc(db, "wellnessPrograms", pair.wellnessProgramId));
                if (!wpDoc.exists()) {
                    log.debug(`[Export] WP NOT FOUND: ${pair.wellnessProgramId}`);
                    setResults(prev => [...prev, `  WP not found: ${pair.wellnessProgramId}`]);
                    continue;
                }
                log.debug(`[Export] Fetched WP: ${wpDoc.id}, old userId=${wpDoc.data()?.userId}`);
                const wpData = wpDoc.data();
                const { firstName, lastName } = anonymizeName();

                const demoWpId = `demo-wellness-program-${origIdx + 1}`;
                const demoUserId = `demo-user-${origIdx + 1}`;
                const transformedUser = {
                    ...userData,
                    id: demoUserId,
                    firstName,
                    lastName,
                    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@demo.com`,
                    organizationId: DEMO_ORGANIZATION_ID,
                    groupId: DEMO_GROUP_ID,
                    groupName: DEMO_GROUP_NAME,
                    wellnessProgramId: demoWpId,
                };

                const transformedWP = {
                    ...wpData,
                    id: demoWpId,
                    userId: demoUserId,
                };
                log.debug(`[Export] Transformed WP: new id=${demoWpId}, new userId=${demoUserId}, correctiveExerciseId=${wpData.correctiveExerciseId}, focusArticleId=${wpData.focusArticleId}`);

                const transformedCPs: any[] = [];
                if (wpData.correctiveExerciseId) {
                    const cpDoc = await getDoc(doc(db, "userCorrectiveExercises", wpData.correctiveExerciseId));
                    if (cpDoc.exists()) {
                        transformedCPs.push({
                            ...cpDoc.data(),
                            id: `demo-corrective-program-${origIdx + 1}`,
                            userId: demoUserId,
                        });
                    }
                }

                const transformedFAs: any[] = [];
                if (wpData.focusArticleId) {
                    const faDoc = await getDoc(doc(db, "userMskFocusArticles", wpData.focusArticleId));
                    if (faDoc.exists()) {
                        transformedFAs.push({
                            ...faDoc.data(),
                            id: `demo-focus-articles-${origIdx + 1}`,
                            userId: demoUserId,
                        });
                    }
                }

                const mskQuery = query(collection(db, "mskScores"), where("userId", "==", pair.userId));
                const mskSnap = await getDocs(mskQuery);
                const transformedMSKs = mskSnap.docs.map((d, scoreIdx) => ({
                    ...d.data(),
                    id: `demo-msk-score-${origIdx + 1}-${scoreIdx + 1}`,
                    userId: demoUserId,
                }));

                baseUsers.push(transformedUser);
                baseWPs.push(transformedWP);
                baseMsks.push(transformedMSKs);
                baseCPs.push(transformedCPs);
                baseFAs.push(transformedFAs);

                setResults(prev => [...prev, `  Base ${origIdx + 1}: 1 user, 1 WP, ${mskSnap.size} MSK, ${transformedCPs.length} CP, ${transformedFAs.length} FA`]);
            }

            // Now generate all output by cycling through base data
            log.debug(`[Export] Starting duplicate loop: totalCount=${totalCount}, pairs.length=${pairs.length}`);
            for (let i = 0; i < totalCount; i++) {
                const baseIdx = i % pairs.length;
                const newIdx = i + 1;
                log.debug(`[Export] Duplicate ${i}: baseIdx=${baseIdx}, newIdx=${newIdx}`);

                const baseUser = baseUsers[baseIdx];
                const baseWP = baseWPs[baseIdx];
                const baseMsksArr = baseMsks[baseIdx];
                const baseCPsArr = baseCPs[baseIdx];
                const baseFAsArr = baseFAs[baseIdx];

                const newUserId = `demo-user-${newIdx}`;
                const newWpId = `demo-wellness-program-${newIdx}`;

                const { firstName, lastName } = anonymizeName();
                const newEmail = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@demo.com`;

                users.push({
                    ...baseUser,
                    id: newUserId,
                    firstName,
                    lastName,
                    email: newEmail,
                    wellnessProgramId: newWpId,
                });

                wellnessPrograms.push({
                    ...baseWP,
                    id: newWpId,
                    userId: newUserId,
                });

                baseMsksArr.forEach((msk: any, mskIdx: number) => {
                    mskScores.push({
                        ...msk,
                        id: `demo-msk-score-${newIdx}-${mskIdx + 1}`,
                        userId: newUserId,
                    });
                });

                baseCPsArr.forEach((cp: any) => {
                    correctivePrograms.push({
                        ...cp,
                        id: `demo-corrective-program-${newIdx}`,
                        userId: newUserId,
                    });
                });

                baseFAsArr.forEach((fa: any) => {
                    focusArticles.push({
                        ...fa,
                        id: `demo-focus-articles-${newIdx}`,
                        userId: newUserId,
                    });
                });
            }

            setExportedData({ users, wellnessPrograms, mskScores, correctivePrograms, focusArticles });
            setResults(prev => [...prev, `Export complete: ${pairs.length} base + ${dupCount} duplicates = ${totalCount} total (${users.length} users, ${wellnessPrograms.length} WPs, ${mskScores.length} MSK, ${correctivePrograms.length} CP, ${focusArticles.length} FA)`]);

        } catch (error: any) {
            setResults(prev => [...prev, `Error: ${error.message}`]);
        }

        setIsExporting(false);
    };

    const downloadFile = (filename: string, content: string) => {
        const blob = new Blob([content], { type: "text/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadAll = () => {
        if (!exportedData) return;

        const content = JSON.stringify({
            users: exportedData.users.map(serializeValue),
            wellnessPrograms: exportedData.wellnessPrograms.map(serializeValue),
            mskScores: exportedData.mskScores.map(serializeValue),
            correctivePrograms: exportedData.correctivePrograms.map(serializeValue),
            focusArticles: exportedData.focusArticles.map(serializeValue)
        }, null, 2);

        downloadFile("exported-demo-data.json", content);
    };

    return (
        <div className={COMPONENT_CLASS} style={{ maxWidth: 800 }}>
            <h3>Export Production Users to Demo</h3>
            <p style={{ marginBottom: 16 }}>
                Enter userId, wellnessProgramId pairs (one per line) to export from Firebase.
            </p>

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>User ID / Wellness Program ID:</label>
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="userId1, wellnessProgramId1"
                    style={{ width: "100%", height: 150, padding: 8, fontFamily: "monospace" }}
                />
            </div>

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8 }}>Number of Duplicates:</label>
                <input
                    type="number"
                    min="0"
                    value={duplicates}
                    onChange={(e) => setDuplicates(parseInt(e.target.value, 10) || 0)}
                    placeholder="0"
                    style={{ padding: 8, width: 100 }}
                />
            </div>

            <div style={{ marginBottom: 16, fontSize: 12, color: "#666" }}>
                Format: userId, wellnessProgramId (comma or tab separated)
            </div>

            <button
                onClick={exportUsers}
                disabled={isExporting}
                style={{ padding: "10px 20px", cursor: isExporting ? "not-allowed" : "pointer", marginRight: 16 }}
            >
                {isExporting ? "Exporting..." : "Export Users"}
            </button>

            {exportedData && (
                <button
                    onClick={downloadAll}
                    style={{ padding: "10px 20px", cursor: "pointer" }}
                >
                    Download JSON
                </button>
            )}

            {results.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <h4>Results:</h4>
                    <div style={{
                        maxHeight: 300,
                        overflowY: "auto",
                        border: "1px solid #ccc",
                        padding: 10,
                        fontSize: 12,
                        fontFamily: "monospace"
                    }}>
                        {results.map((result, index) => (
                            <div key={index} style={{ marginBottom: 4 }}>
                                {result}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExportProductionUsersToDemoTool;